import CancelChangesModal from '@/components/common/CancelChangesModal/cancelChangesModal';
import { IAlbum } from '@/data/models/album';
import { IOptions } from '@/data/models/option';
import { IValidationResults } from '@/data/models/validation';
import { useProductEditMode } from '@/hooks/useProductEditMode';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import 'react-tabs/style/react-tabs.css';
import ValidationErrors from '../ProductView/ValidationErrors/ValidationErrors';
import { NestedModalContext } from '@/context/NestedModalContext';
import {
  AccountExclusiveReleaseFormGroup,
  AdditionalInformationFormGroup,
  ArtistFormGroup,
  BoxCountFormGroup,
  CatalogNumberFormGroup,
  ColorFormGroup,
  CountryOfManufactureFormGroup,
  GenreFormGroup,
  GtinFormGroup,
  InternalDescriptionFormGroup,
  LabelFormGroup,
  ManufacturedByVmgFormGroup,
  ManufacturingCostFormGroup,
  MerchandiseDisplayNameFormGroup,
  MerchandiseTypeFormGroup,
  PackagingDescriptionFormGroup,
  PackagingFormGroup,
  ProductDescriptionFormGroup,
  ProductVersionsFormGroup,
  ProjectedPhysicalIOFormGroup,
  RetailPreOrderDateFormGroup,
  SizeFormGroup,
  ReleaseDateFormGroup,
  TitleFormGroup,
  UnitsPerSetFormGroup,
} from './FormGroup';
import './ProductDetailsEdit.scss';
import useFindErrorMessage from './useFindErrorMessage';
import { FormGroupProps } from './FormGroup/types';
import { IChangeRequest } from '@/data/models/changeRequest';
import { ICurrentProductMetadataChangesGroup } from '@/types/product-current-changes-types';
import ChangeRequestIndicator from '@/components/common/ChangeRequestIndicator/ChangeRequestIndicator';
import DismissibleErrorAlert from '@/components/common/ErrorAlert/DismissibleErrorAlert';

interface Props {
  togglePopUp: any;
  albumObj: IAlbum;
  errorFlag: boolean;
  errors: IValidationResults[];
  readonly: boolean;
  showValidationErrors: boolean;
  onProductUpdated: () => void;
  changeRequest?: IChangeRequest;
  currentChanges?: ICurrentProductMetadataChangesGroup;
}

const MerchProductDetailsEdit = ({
  togglePopUp,
  albumObj,
  errorFlag,
  errors,
  readonly,
  showValidationErrors,
  onProductUpdated,
  changeRequest,
  currentChanges,
}: Props) => {
  const { t } = useTranslation();
  const {
    album,
    setAlbum,
    packagings,
    merchandiseTypes,
    genres,
    territories,
    albumValidationMetadata,
    errorMessages,
    errorBanner,
    saveErrorMessage,
    setSaveErrorMessage,
    showPopUp,
    showCancelChangesModal,
    setShowCancelChangesModal,
    isSaveDisabled,
    isSubmitting,
    albumErrors,
    albumIssues,
    showErrors,
    onChangePreorderDate,
    setOnChangePreorderDate,
    onClickTargetAccount,
    setOnClickTargetAccount,
    onChangeTargetAccount,
    setOnChangeTargetAccount,
    yesOption,
    noOption,
    loadLabels,
    loadPackaging,
    loadMerchandiseTypes,
    loadGenres,
    loadTerritories,
    handleManufacturing,
    handleManufacturingCostBlur,
    handleRadioButton,
    handleAlbumMetaData,
    handleMerchandiseMetadata,
    handleSaleProjection,
    handleAlbumField,
    handleSubmit,
    handleGenre,
    handleVersion,
    handleArtist,
    handleAccountExclusiveChange,
    handleLabelSelect,
    onCancel,
    closePopUp,
    handlePackaging,
    getPreviousReleaseDate,
    fieldHasErrors,
    fieldValidationState,
  } = useProductEditMode({
    togglePopUp,
    albumObj,
    errorFlag,
    errors,
    showValidationErrors,
    onProductUpdated,
    changeRequest,
  });

  const findErrorMessage = useFindErrorMessage(albumErrors);

  const [isNestedModalOpen, setNestedModalOpen] = useState(false);

  const artistRoles = [
    {
      value: 'main',
      label: 'Main',
    },
  ] as const satisfies IOptions[];

  useEffect(() => {
    const keyDownHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (isNestedModalOpen) return;
        onCancel();
      }
    };
    document.addEventListener('keydown', keyDownHandler);
    return () => {
      document.removeEventListener('keydown', keyDownHandler);
    };
  }, [onCancel, isNestedModalOpen]);

  const formGroupProps: FormGroupProps = {
    readonly,
    album,
    showValidationErrors: showErrors,
    fieldHasErrors,
    fieldValidationState,
    findErrorMessage,
  };

  const isChangeRequest = !!changeRequest;

  return (
    <NestedModalContext.Provider
      value={{ isNestedModalOpen, setNestedModalOpen }}
    >
      <CancelChangesModal
        onSubmit={handleSubmit}
        onClose={closePopUp}
        onCancel={() => setShowCancelChangesModal(false)}
        show={showCancelChangesModal}
        titleText={t('product.editModeProduct.title')}
        bodyText={t('product.editModeProduct.cancelChangesBody')}
      />
      <form
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          const submitter = (e.nativeEvent as SubmitEvent)
            .submitter as HTMLElement | null;
          if (submitter && !submitter.classList.contains('buttonSave')) {
            return; // Ignore submissions bubbled up from nested modal buttons
          }
          handleSubmit();
        }}
      >
        {showPopUp ? (
          <div
            data-testid="createProduct"
            id="productModal"
            className="product-edit-mode-modal"
          >
            <div className="product-edit-mode-modal-content">
              <div className="product-edit-mode-modal-header">
                <button
                  className="btn-close"
                  type="button"
                  aria-label="Close"
                  onClick={onCancel}
                >
                  <i className="icon fas fa-times fa-lg" aria-hidden="true"></i>
                </button>
                <h2>
                  {!readonly
                    ? t('product.editModeProduct.title')
                    : t('product.editModeProduct.productDetails')}
                </h2>
                <div className="edit-modal-spacer"></div>
              </div>

              <div
                className={`product-edit-mode-modal-body ${readonly ? 'readonly-modal' : ''}`}
              >
                <div className="scroll-body">
                  <DismissibleErrorAlert
                    error={saveErrorMessage}
                    onClose={() => setSaveErrorMessage(null)}
                  />
                  {!readonly ? (
                    <ValidationErrors
                      errorBanner={errorBanner}
                      errors={errorMessages}
                      validationErrors={albumIssues}
                      region={'Edit'}
                    ></ValidationErrors>
                  ) : null}
                  <div className="edit-form">
                    {/* Title */}
                    <ChangeRequestIndicator
                      show={currentChanges?.title?.hasChanges}
                      displayValue={currentChanges?.title?.value}
                    ></ChangeRequestIndicator>
                    <TitleFormGroup
                      {...formGroupProps}
                      labelTitle={t('product.editProduct.name')}
                      handleAlbumField={handleAlbumField}
                    />

                    {/* Display Name */}
                    <ChangeRequestIndicator
                      show={currentChanges?.displayName?.hasChanges}
                      displayValue={currentChanges?.displayName?.value}
                    ></ChangeRequestIndicator>
                    <MerchandiseDisplayNameFormGroup
                      {...formGroupProps}
                      handleMerchandiseMetadata={handleMerchandiseMetadata}
                    />

                    {/* Version */}
                    <ChangeRequestIndicator
                      show={currentChanges?.versions?.hasChanges}
                      displayValue={currentChanges?.versions?.value}
                    ></ChangeRequestIndicator>
                    <ProductVersionsFormGroup
                      {...formGroupProps}
                      handleVersion={handleVersion}
                      maxVersions={1}
                    />

                    {/* Merchandise Type */}
                    <ChangeRequestIndicator
                      show={currentChanges?.merchandiseType?.hasChanges}
                      displayValue={currentChanges?.merchandiseType?.value}
                      customObjectKeys={['name']}
                    ></ChangeRequestIndicator>
                    <MerchandiseTypeFormGroup
                      {...formGroupProps}
                      merchandiseTypes={merchandiseTypes ?? []}
                      loadMerchandiseTypes={loadMerchandiseTypes}
                      handleMerchandiseMetadata={handleMerchandiseMetadata}
                    />

                    {/* Color */}
                    <ChangeRequestIndicator
                      show={currentChanges?.color?.hasChanges}
                      displayValue={currentChanges?.color?.value}
                    ></ChangeRequestIndicator>
                    <ColorFormGroup
                      {...formGroupProps}
                      handleMerchandiseMetadata={handleMerchandiseMetadata}
                    />

                    {/* Size */}
                    <ChangeRequestIndicator
                      show={currentChanges?.size?.hasChanges}
                      displayValue={currentChanges?.size?.value}
                    ></ChangeRequestIndicator>
                    <SizeFormGroup
                      {...formGroupProps}
                      handleMerchandiseMetadata={handleMerchandiseMetadata}
                    />

                    {/* Internal Description */}
                    <ChangeRequestIndicator
                      show={currentChanges?.internalDescription?.hasChanges}
                      displayValue={currentChanges?.internalDescription?.value}
                    ></ChangeRequestIndicator>
                    <InternalDescriptionFormGroup
                      {...formGroupProps}
                      handleAlbumMetaData={handleAlbumMetaData}
                    />

                    {/* Artist*/}
                    <div className="change-indicator-field">
                      <ChangeRequestIndicator
                        show={currentChanges?.artists?.hasChanges}
                        displayValue={currentChanges?.artists?.value}
                        customObjectKeys={['name']}
                        customClass="merch-artist-change-indicator"
                      ></ChangeRequestIndicator>
                      <ArtistFormGroup
                        {...formGroupProps}
                        errorBanner={errorBanner}
                        labelTitle={t('product.editProduct.artistBrand')}
                        handleArtist={handleArtist}
                        artistRoles={artistRoles}
                      />
                    </div>

                    <LabelFormGroup
                      {...formGroupProps}
                      loadLabels={loadLabels}
                      handleLabelSelect={handleLabelSelect}
                      disabled={isChangeRequest}
                    />

                    <GtinFormGroup
                      {...formGroupProps}
                      handleAlbumField={handleAlbumField}
                      disabled={isChangeRequest}
                      displayValueOnly={isChangeRequest}
                    />

                    {/* Catalog Number */}
                    <ChangeRequestIndicator
                      show={currentChanges?.catalogNumber?.hasChanges}
                      displayValue={currentChanges?.catalogNumber?.value}
                    ></ChangeRequestIndicator>
                    <CatalogNumberFormGroup
                      {...formGroupProps}
                      errorBanner={errorBanner}
                      labelTitle={t('product.editProduct.catalog#')}
                      handleAlbumField={handleAlbumField}
                      disabled={isChangeRequest}
                    />

                    {/* Genre */}
                    <ChangeRequestIndicator
                      show={currentChanges?.genre?.hasChanges}
                      displayValue={currentChanges?.genre?.value}
                      customObjectKeys={['masterGenre', 'name']}
                      customSeparator={' > '}
                    ></ChangeRequestIndicator>
                    <GenreFormGroup
                      {...formGroupProps}
                      genres={genres ?? []}
                      loadGenres={loadGenres}
                      handleGenre={handleGenre}
                    />

                    {/* Manufactured by Ingrooves */}
                    <ChangeRequestIndicator
                      show={
                        currentChanges?.isManufacturedByIngrooves?.hasChanges
                      }
                      displayValue={
                        currentChanges?.isManufacturedByIngrooves?.value
                      }
                    ></ChangeRequestIndicator>
                    <ManufacturedByVmgFormGroup
                      {...formGroupProps}
                      handleRadioButton={handleRadioButton}
                    />

                    {/* Release Date */}
                    <ChangeRequestIndicator
                      show={currentChanges?.releaseDate?.hasChanges}
                      displayValue={currentChanges?.releaseDate?.value}
                    ></ChangeRequestIndicator>
                    <ReleaseDateFormGroup
                      {...formGroupProps}
                      albumValidationMetadata={albumValidationMetadata}
                      showValidationErrors={showValidationErrors}
                      albumErrors={albumErrors}
                      onChange={(releaseDate) =>
                        setAlbum({ ...album, releaseDate })
                      }
                    />

                    {/* Exclusive Release Account */}
                    <ChangeRequestIndicator
                      show={currentChanges?.accountExclusive?.hasChanges}
                      displayValue={[
                        currentChanges?.accountExclusive?.value
                          ?.exclusiveReleaseAccount,
                        currentChanges?.accountExclusive?.value
                          ?.exclusiveReleaseAccountName,
                      ]}
                    ></ChangeRequestIndicator>
                    <AccountExclusiveReleaseFormGroup
                      {...formGroupProps}
                      albumErrors={albumErrors}
                      onChangeTargetAccount={onChangeTargetAccount}
                      onClickTargetAccount={onClickTargetAccount}
                      yesOption={yesOption}
                      noOption={noOption}
                      setOnClickTargetAccount={setOnClickTargetAccount}
                      setOnChangeTargetAccount={setOnChangeTargetAccount}
                      handleAccountExclusiveChange={
                        handleAccountExclusiveChange
                      }
                      handleAlbumMetaData={handleAlbumMetaData}
                    />

                    {/* Preorder Date/DateTime */}
                    <ChangeRequestIndicator
                      show={currentChanges?.retailPreOrder?.hasChanges}
                      displayValue={currentChanges?.retailPreOrder?.value}
                    ></ChangeRequestIndicator>
                    <RetailPreOrderDateFormGroup
                      {...formGroupProps}
                      albumErrors={albumErrors}
                      onChangePreorderDate={onChangePreorderDate}
                      setOnChangePreorderDate={setOnChangePreorderDate}
                      handleAlbumMetaData={handleAlbumMetaData}
                      getPreviousReleaseDate={getPreviousReleaseDate}
                    />

                    {/* Packaging */}
                    <ChangeRequestIndicator
                      show={currentChanges?.packaging?.hasChanges}
                      displayValue={currentChanges?.packaging?.value?.name}
                    ></ChangeRequestIndicator>
                    <PackagingFormGroup
                      {...formGroupProps}
                      packagings={packagings ?? []}
                      loadPackaging={loadPackaging}
                      handlePackaging={handlePackaging}
                    />

                    {/* Packaging Description */}
                    <ChangeRequestIndicator
                      show={currentChanges?.packagingDescription?.hasChanges}
                      displayValue={currentChanges?.packagingDescription?.value}
                    ></ChangeRequestIndicator>
                    <PackagingDescriptionFormGroup
                      {...formGroupProps}
                      handleAlbumMetaData={handleAlbumMetaData}
                    />

                    {/* Country of Manufacture */}
                    <ChangeRequestIndicator
                      show={currentChanges?.countryOfManufacture?.hasChanges}
                      displayValue={
                        territories?.find(
                          (t) =>
                            t.value ===
                            currentChanges?.countryOfManufacture?.value
                        )?.label
                      }
                    ></ChangeRequestIndicator>
                    <CountryOfManufactureFormGroup
                      {...formGroupProps}
                      territories={territories ?? []}
                      loadTerritories={loadTerritories}
                      handleManufacturing={handleManufacturing}
                    />

                    {/* Currency Manufacturing Cost Per Unit */}
                    <ChangeRequestIndicator
                      show={
                        currentChanges?.currencyManufacturingCostPerUnit
                          ?.hasChanges
                      }
                      displayValue={
                        currentChanges?.currencyManufacturingCostPerUnit?.value
                      }
                      customObjectKeys={[
                        'currency',
                        'manufacturingCostPerUnit',
                      ]}
                      customSeparator={' '}
                    ></ChangeRequestIndicator>
                    <ManufacturingCostFormGroup
                      {...formGroupProps}
                      handleManufacturing={handleManufacturing}
                      handleManufacturingCostBlur={handleManufacturingCostBlur}
                    />

                    {/* Units Per Sets */}
                    <ChangeRequestIndicator
                      show={currentChanges?.unitsPerSets?.hasChanges}
                      displayValue={currentChanges?.unitsPerSets?.value}
                    ></ChangeRequestIndicator>
                    <UnitsPerSetFormGroup
                      {...formGroupProps}
                      handleAlbumMetaData={handleAlbumMetaData}
                    />

                    {/* Box Count */}
                    <ChangeRequestIndicator
                      show={currentChanges?.boxCount?.hasChanges}
                      displayValue={currentChanges?.boxCount?.value}
                    ></ChangeRequestIndicator>
                    <BoxCountFormGroup
                      {...formGroupProps}
                      handleAlbumMetaData={handleAlbumMetaData}
                    />

                    {/* Projected Physical IO */}
                    <ChangeRequestIndicator
                      show={currentChanges?.projectedPhysicalIO?.hasChanges}
                      displayValue={currentChanges?.projectedPhysicalIO?.value}
                    ></ChangeRequestIndicator>
                    <ProjectedPhysicalIOFormGroup
                      {...formGroupProps}
                      handleSaleProjection={handleSaleProjection}
                    />

                    {/* Product Description */}
                    <ChangeRequestIndicator
                      show={currentChanges?.productDescription?.hasChanges}
                      displayValue={currentChanges?.productDescription?.value}
                    ></ChangeRequestIndicator>
                    <ProductDescriptionFormGroup
                      {...formGroupProps}
                      handleMerchandiseMetadata={handleMerchandiseMetadata}
                    />

                    {/* Additional Information */}
                    <ChangeRequestIndicator
                      show={currentChanges?.additionalInformation?.hasChanges}
                      displayValue={
                        currentChanges?.additionalInformation?.value
                      }
                    ></ChangeRequestIndicator>
                    <AdditionalInformationFormGroup
                      {...formGroupProps}
                      handleMerchandiseMetadata={handleMerchandiseMetadata}
                    />
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <div className="edit-modal-spacer"></div>
                <div className="modal-footer-buttons">
                  {!readonly ? (
                    <>
                      <button
                        type="button"
                        className="btn btn-primary alt"
                        onClick={onCancel}
                        disabled={isSubmitting}
                      >
                        {t('common.button.cancel')}
                      </button>
                      <button
                        type="submit"
                        data-testid="Save"
                        className="btn btn-primary buttonSave"
                        disabled={isSaveDisabled || isSubmitting}
                      >
                        {t('common.button.save')}
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={closePopUp}
                    >
                      {t('common.button.close')}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </form>
    </NestedModalContext.Provider>
  );
};

export default MerchProductDetailsEdit;
